import { useState } from 'react';

import { openLink } from '@/shared/lib/telegram';
import { RADIUS, Squircle, StaticSquircle } from '@/shared/ui';

import styles from './FaqList.module.scss';

/**
 * Абзац, маркированный список или абзац со ссылкой в конце.
 *
 * Ссылка отдельным видом блока, а не разметкой внутри строки: тексты
 * приходят от заказчика простым текстом, и разбирать в них HTML значило бы
 * пускать чужую разметку в интерфейс.
 */
type Block =
  | string
  | { readonly list: readonly string[] }
  | { readonly text: string; readonly linkLabel: string; readonly href: string };

// Тексты — документ FAQ заказчика (сверка 06.10.2026, до этого —
// «Копирайты BETBOOM» от 28.08.2026). Правятся только вместе с этим
// документом: FAQ обещает игроку условия акции, и расхождение с ним
// дороже удачной формулировки.
//
// Отступления от документа — только опечатки и одно уточнение: у сброса
// Очков месяца указано и время, 00:00 МСК, — так считает код.
const QUESTIONS: readonly {
  readonly key: string;
  readonly question: string;
  readonly answer: readonly Block[];
}[] = [
  {
    key: 'how-to-play',
    question: 'Как играть?',
    answer: [
      'Общайся в чате, пиши комментарии и ставь реакции, так ты откроешь доступ к прогнозам. Прогнозируй победителя матча и счёт. Угадал – получай очки и становись ближе к призам. Не угадал – не страшно, повезёт в следующий раз. Ещё можно отвечать на футбольные вопросы в режиме «Дерби».',
    ],
  },
  {
    key: 'chat-commands',
    question: 'Какие команды доступны в чате?',
    answer: [
      'В боте BETBOOM Путь Чемпионов доступны команды:',
      {
        list: [
          '/subscription – проверка подписки на телеграм-канал',
          '/freebets – узнать количество фрибетов и что сделать, чтобы вывести их на аккаунт BETBOOM',
          '/achievements – узнать, какие ачивки ты получил за время этой ЛЧ',
          '/phone – проверить, поделился ли ты своим номером, чтобы выводить фрибеты и участвовать в розыгрышах',
          '/changephone – сменим номер телефона, если ты случайно ввёл другой',
          '/score – узнать, сколько у тебя очков и какие места ты занимаешь в Недельном и Месячном рейтингах',
          '/help – пиши её, если забыл какую-то из доступных команд',
          '/share – выдам тебе реферальную ссылку, чтобы ты пригласил друзей и получил очки',
        ],
      },
      'В чате BETBOOM доступны команды:',
      { list: ['/achievements', '/score', '/help'] },
    ],
  },
  {
    key: 'prizes',
    question: 'Что за призы и как их выиграть?',
    answer: [
      {
        list: [
          'Главный приз – поездка в город победителя ЛЧ. Разыгрывается в конце сезона ЛЧ',
          'В Недельном рейтинге можно гарантированно выиграть Фрибеты, заняв 1-15 место',
          'В Месячном розыгрыше Фрибеты и Физический приз* разыгрываются случайным образом среди топ-1000 игроков',
        ],
      },
      '*Физический приз можно обменять на эквивалент во фрибетах. Подробности уточняйте в чате BETBOOM',
    ],
  },
  {
    key: 'why-points',
    question: 'Зачем нужны Очки?',
    answer: [
      'Это основной нерасходуемый игровой ресурс прогрессии. Есть два типа: Очки недели и Очки месяца:',
      {
        list: [
          'Очки Недельного цикла. Используются для определения позиции в Недельном рейтинге. Сбрасываются каждый понедельник в 00:00 МСК',
          'Очки Месячного цикла. Используются для определения позиции в Месячном рейтинге и прогресса текущей Главы в Карьере. Сбрасываются при начале нового Месячного цикла — 8 числа каждого месяца в 00:00 МСК',
        ],
      },
    ],
  },
  {
    key: 'what-predictions',
    question: 'Что такое Прогнозы?',
    answer: [
      'Игровая активность, в которой ты выбираешь предполагаемый результат матча и подтверждаешь его, расходуя 10 Фанкоинов на каждый отдельный прогноз. За успешный прогноз начисляются очки. За неуспешный прогноз очки не начисляются, штрафов нет',
      'Прогнозы доступны не постоянно, а в рамках ограниченных по времени серий прогнозов, связанных с проведением футбольных матчей. Серия прогнозов начинается за несколько дней до соответствующего блока матчей и продолжается в дни их проведения',
    ],
  },
  {
    key: 'earn-points',
    question: 'Как заработать Очки?',
    answer: [
      'В Очки недели входят Очки, полученные в течение текущей недели за:',
      {
        list: [
          'Успешные Прогнозы',
          'Прохождение Дерби',
          'Активность в сообществе',
          'Выполнение Ачивок',
          'Очки за Популярный пост',
          'Очки за Супер-пост',
        ],
      },
      'В Очки месяца входят Очки, полученные в течение Месячного цикла за:',
      {
        list: [
          'Успешные Прогнозы',
          'Прохождение Дерби',
          'Активность в сообществе',
          'Выполнение Ачивок',
          'Призовые места в Недельных турнирах',
          'Очки за Популярный пост',
          'Очки за Супер-пост',
        ],
      },
    ],
  },
  {
    key: 'withdraw-freebets',
    question: 'Как вывести Фрибеты?',
    answer: [
      'Чтобы вывести фрибеты на свой аккаунт BETBOOM, надо:',
      {
        list: [
          'Подписаться на телеграм BETBOOM',
          'Поделиться номером с ботом BETBOOM Путь Чемпионов',
        ],
      },
      'Если выполнил оба условия и на счету есть фрибеты, переходи в бота BETBOOM Путь Чемпионов, пиши команду /freebets и следуй инструкциям',
    ],
  },
  {
    key: 'what-derby',
    question: 'Что такое Дерби?',
    answer: [
      'Игровая активность с вопросами об истории ЛЧ и футбольных событиях. Участие в Дерби бесплатно и не требует игровых ресурсов. Дерби запускается, когда в ЛЧ перерыв и не на что делать прогнозы. Состоит из 5 вопросов. Для каждого тебе предлагается два варианта ответа. Перепройти Дерби нельзя',
      'Зайти в Дерби можно через главную страницу мини-аппа, нажав кнопку «Играть» (та же кнопка, по которой ты заходишь в прогнозы)',
    ],
  },
  {
    key: 'derby-reward',
    question: 'Что я получу за Дерби?',
    answer: [
      'За каждый правильный ответ в Дерби начисляется 20 очков, то есть максимум за 1 Дерби можно получить 100 очков, дав 5 правильных ответов',
    ],
  },
  {
    key: 'what-funcoins',
    question: 'Что такое Фанкоины?',
    answer: [
      'Это расходуемый игровой ресурс, используемый для совершения Прогнозов. Каждый Прогноз имеет фиксированную стоимость: 1 Прогноз = 10 Фанкоинов',
      'Все типы Прогнозов имеют одинаковую стоимость. Неиспользованные Фанкоины остаются на твоём счету, пока ты не потратишь их на прогнозы',
    ],
  },
  {
    key: 'earn-funcoins',
    question: 'Как заработать Фанкоины?',
    answer: [
      'Фанкоины начисляются за продвижение в Карьере. При достижении контрольной точки в Карьере, установленное количество Фанкоинов автоматически добавится на твой счёт',
    ],
  },
  {
    key: 'popular-and-super-post',
    question: 'Что такое Популярный пост и Супер-пост?',
    answer: [
      'Популярный пост. Если пост в Telegram-канале BETBOOM набирает 250 реакций, он считается популярным, а все игроки получают по 10 Очков. За один и тот же пост награда начисляется только один раз',
      'Супер-пост. Если пост в Telegram-канале BETBOOM набирает 300 реакций, он считается супер-постом, а 15 случайных игроков получают по 40 Очков. За один и тот же пост награда начисляется только один раз',
    ],
  },
  {
    key: 'successful-referral',
    question: 'Кто такой Успешный реферал?',
    answer: [
      'Это пользователь, который попал в игру по вашей реферальной ссылке и набрал 300+ Очков',
    ],
  },
  {
    key: 'boost-freebets',
    question: 'Как получить Фрибеты за бусты?',
    // Бустов всего 4: первый — «Бустер», ещё три — «Меценат» (уточнение
    // 06.10.2026), поэтому «сразу 4 буста» — это обе Ачивки
    answer: [
      'За 1 буст Telegram-канала BETBOOM дадим тебе 100 Фрибетов, за 3 буста ещё 400 Фрибетов. Если сразу отдашь каналу 4 буста, начислим 500 Фрибетов. В конце Игрового месяца смотрим, есть ли твои бусты. Если есть – оставляем Фрибеты на счету. Если нет – снимаем со счёта',
      '*За 2 буста дополнительные Фрибеты не начисляются',
    ],
  },
  {
    key: 'game-month',
    question: 'Сколько длится Игровой месяц?',
    answer: ['Игровой месяц длится с 8 по 8 число каждого месяца'],
  },
  {
    key: 'detailed-rules',
    question: 'А есть подробные правила?',
    // Правила получены от заказчика 04.09.2026 и лежат в Google Docs.
    //
    // Адрес — вид `/view`, а НЕ `/edit`, каким его прислали: ссылка уходит
    // всем игрокам, и приглашать их в редактор юридического документа
    // незачем. Параметры `usp`, `ouid` и прочие отброшены — `ouid` это
    // идентификатор аккаунта владельца, игрокам он не нужен.
    answer: [
      {
        text: 'Да, юридические правила проведения акции читайте ',
        linkLabel: 'тут',
        href: 'https://docs.google.com/document/d/1KWj03wEF0tneCRu-SFtbG7uO_YiGDYJ1/view',
      },
    ],
  },
];

/** «Частые вопросы» на главном экране (узел 438:48425). */
export function FaqList() {
  // Первый вопрос раскрыт сразу (узел 438:48425)
  const [openKey, setOpenKey] = useState<string | null>(
    QUESTIONS[0]?.key ?? null,
  );

  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.root}>
      <h2 className={styles.title}>Частые вопросы</h2>

      <div className={styles.list}>
        {QUESTIONS.map((item) => {
          const isOpen = openKey === item.key;
          return (
            <Squircle
              cornerRadius={RADIUS.xl}
              className={styles.item}
              key={item.key}
              data-testid={`faq-${item.key}`}
            >
              <button
                type="button"
                className={styles.question}
                aria-expanded={isOpen}
                onClick={() => {
                  setOpenKey(isOpen ? null : item.key);
                }}
              >
                {item.question}
                <StaticSquircle
                  asChild
                  width={24}
                  height={24}
                  cornerRadius={RADIUS.sm}
                >
                  <span className={styles.chevronBox}>
                    <img
                      // chevron-open.svg смотрит вниз, chevron-closed.svg — вверх
                      // (имена — про пиктограмму, не про направление): раскрытая
                      // карточка показывает «вверх», свёрнутая — «вниз».
                      src={
                        isOpen
                          ? '/images/icons/chevron-closed.svg'
                          : '/images/icons/chevron-open.svg'
                      }
                      alt=""
                      aria-hidden
                      className={styles.chevron}
                    />
                  </span>
                </StaticSquircle>
              </button>
              {isOpen ? (
                <div className={styles.answer}>
                  {item.answer.map((block, i) => {
                    if (typeof block === 'string') {
                      return (
                        <p className={styles.paragraph} key={i}>
                          {block}
                        </p>
                      );
                    }
                    if ('list' in block) {
                      return (
                        <ul className={styles.bullets} key={i}>
                          {block.list.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p className={styles.paragraph} key={i}>
                        {block.text}
                        {/* Не <a>: внутри WebView Telegram внешние адреса
                            открывает клиент через `openLink`, иначе переход
                            уводит из мини-аппа или молча не срабатывает */}
                        <button
                          type="button"
                          className={styles.link}
                          data-testid="faq-rules-link"
                          onClick={() => {
                            openLink(block.href);
                          }}
                        >
                          {block.linkLabel}
                        </button>
                      </p>
                    );
                  })}
                </div>
              ) : null}
            </Squircle>
          );
        })}
      </div>
    </Squircle>
  );
}
